import { transmute, type FieldSpec } from '@/shared/mapping';
import { genderDisplayText } from '@/shared/enums/gender';

import type { CaseDetailDto } from '../dto/case.dto';

type RawRecord = Record<string, unknown>;

function isRecord(value: unknown): value is RawRecord {
  return typeof value === 'object' && value !== null;
}

function asString(value: unknown): string {
  return String(value ?? '').trim();
}

function asBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  const normalized = asString(value).toLowerCase();
  return (
    normalized === 'true' ||
    normalized === '1' ||
    normalized === 'yes' ||
    normalized === '是'
  );
}

function pickName(value: unknown): string {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return String(value);
  if (!isRecord(value)) return '';
  for (const key of ['name', 'fullname', 'title', 'label', 'nickname', 'username']) {
    const field = value[key];
    if (typeof field === 'string' && field.trim()) return field.trim();
    if (typeof field === 'number') return String(field);
  }
  return '';
}

function firstText(values: unknown[]): string {
  for (const value of values) {
    const text = pickName(value);
    if (text) return text;
  }
  return '';
}

function extractArchive(row: RawRecord): RawRecord {
  const raw =
    row.extend ??
    row.case_maindata_archive ??
    row.caseMaindataArchive ??
    row.archive ??
    row.case_archive ??
    row.caseMaindataArchiveRecord;
  const archive = Array.isArray(raw) ? raw[0] : raw;
  return isRecord(archive) ? archive : {};
}

const CASE_DETAIL_BASE_FIELDS: FieldSpec[] = [
  { key: 'id', defaultVal: '', transform: value => asString(value) },
  { key: 'name', defaultVal: '', transform: value => asString(value) },
  { key: 'createdAt', from: 'createdAt', defaultVal: '', transform: (value, row) => asString(value || row.created_at) },
  { key: 'updatedAt', from: 'updatedAt', defaultVal: '', transform: (value, row) => asString(value || row.updated_at) },
];

export class CaseDetailMapper {
  static toDetail(row: unknown): CaseDetailDto | null {
    if (!isRecord(row)) return null;
    const archive = extractArchive(row);
    const base = transmute<Pick<CaseDetailDto, 'id' | 'name' | 'createdAt' | 'updatedAt'>>(
      row,
      CASE_DETAIL_BASE_FIELDS,
    );
    return {
      ...base,
      patientName:
        firstText([
          archive.name,
          archive.patientName,
          archive.patient_name,
          row.name,
        ]) || '—',
      gender: genderDisplayText(
        firstText([archive.gender, archive.patientGender, archive.patient_gender]),
      ),
      age: firstText([archive.age, archive.patientAge, archive.patient_age]),
      bloodType: firstText([archive.bloodType, archive.blood_type]),
      idCard: firstText([
        archive.idCard,
        archive.id_card,
        archive.identityCard,
        archive.identity_card,
      ]),
      outpatientNo: firstText([
        archive.outpatientNo,
        archive.outpatient_no,
        archive.clinicNo,
        archive.clinic_no,
      ]),
      inpatientNo: firstText([
        archive.inpatientNo,
        archive.inpatient_no,
        archive.hospitalNo,
        archive.hospital_no,
      ]),
      isHospitalized: asBoolean(
        archive.isHospitalized ??
          archive.is_hospitalized ??
          archive.hospitalized ??
          archive.inHospital,
      ),
      hospital: firstText([row.hospital, archive.hospital]),
      center: firstText([row.site, archive.center, archive.site]),
      department: firstText([
        row.hospital_department,
        archive.department,
        archive.hospital_department,
      ]),
      departmentCategory: firstText([
        row.hospital_department_category,
        archive.departmentCategory,
        archive.department_category,
        archive.ga_hospital_department_category,
      ]),
      doctor: firstText([row.doctor, archive.doctor]),
      bodyPart: firstText([
        archive.bodyPart,
        archive.bodypart,
        archive.medical_advice_bodypart,
      ]),
      indication: firstText([
        archive.indication,
        archive.ga_indication,
        archive.indicationInfo,
      ]),
      treatmentPlan: firstText([
        archive.treatmentPlan,
        archive.treatment_plan,
        archive.ga_treatment_plan,
        archive.treatment_plan_info,
      ]),
      admissionDiagnosis: firstText([
        archive.admissionDiagnosis,
        archive.admission_diagnosis,
        archive.diagnosis,
      ]),
      province: firstText([archive.province, archive.ga_province]),
      city: firstText([archive.city, archive.ga_city]),
      district: firstText([archive.district, archive.ga_district]),
      town: firstText([archive.town, archive.ga_town]),
      createdBy: firstText([row.createdBy, row.created_by]),
      updatedBy: firstText([row.updatedBy, row.updated_by]),
    };
  }

  static toDetailList(rows: unknown[]): CaseDetailDto[] {
    return (Array.isArray(rows) ? rows : [])
      .map(row => CaseDetailMapper.toDetail(row))
      .filter((row): row is CaseDetailDto => row != null);
  }
}
